import { defineMessages, useIntl } from "react-intl";
import { useCallback } from "react";
import SweetAlert, { SweetAlertOptions } from "sweetalert2";
import { APP_COLORS } from "../../witadmin/Common/constants";

const messages = defineMessages({
  processingTitle: { id: "insurance.modal.processing.title", defaultMessage: "Processing" },
  processingDescription: {
    id: "insurance.modal.processing.description",
    defaultMessage: "Please wait while we process your request.",
  },
  getOneErrorTitle: {
    id: "insurance.modal.getOneError.title",
    defaultMessage: "Error retrieving insurance",
  },
  getOneErrorDescription: {
    id: "insurance.modal.getOneError.description",
    defaultMessage: "There was an error while retrieving the insurance. Please try again.",
  },
  createSuccessTitle: {
    id: "insurance.modal.createSuccess.title",
    defaultMessage: "Insurance created",
  },
  createSuccessDescription: {
    id: "insurance.modal.createSuccess.description",
    defaultMessage: "The insurance has been created successfully.",
  },
  createErrorTitle: {
    id: "insurance.modal.createError.title",
    defaultMessage: "Error creating insurance",
  },
  createErrorDescription: {
    id: "insurance.modal.createError.description",
    defaultMessage: "There was an error while creating the insurance. Please try again.",
  },
  updateSuccessTitle: {
    id: "insurance.modal.updateSuccess.title",
    defaultMessage: "Insurance updated",
  },
  updateSuccessDescription: {
    id: "insurance.modal.updateSuccess.description",
    defaultMessage: "The insurance has been updated successfully.",
  },
  updateErrorTitle: {
    id: "insurance.modal.updateError.title",
    defaultMessage: "Error updating insurance",
  },
  updateErrorDescription: {
    id: "insurance.modal.updateError.description",
    defaultMessage: "There was an error while updating the insurance. Please try again.",
  },
});

type InsuranceModalAction =
  | "get_error"
  | "create_error"
  | "update_error"
  | "create_success"
  | "update_success";

export function useModal() {
  const intl = useIntl();

  const getModalPropsByAction = useCallback(
    (action: InsuranceModalAction): SweetAlertOptions => {
      switch (action) {
        case "get_error":
          return {
            title: intl.formatMessage(messages.getOneErrorTitle),
            text: intl.formatMessage(messages.getOneErrorDescription),
            confirmButtonColor: APP_COLORS.primary,
            icon: "error",
          };
        case "create_success":
          return {
            title: intl.formatMessage(messages.createSuccessTitle),
            text: intl.formatMessage(messages.createSuccessDescription),
            confirmButtonColor: APP_COLORS.primary,
            icon: "success",
          };
        case "create_error":
          return {
            title: intl.formatMessage(messages.createErrorTitle),
            text: intl.formatMessage(messages.createErrorDescription),
            confirmButtonColor: APP_COLORS.primary,
            icon: "error",
          };
        case "update_success":
          return {
            title: intl.formatMessage(messages.updateSuccessTitle),
            text: intl.formatMessage(messages.updateSuccessDescription),
            confirmButtonColor: APP_COLORS.primary,
            icon: "success",
          };
        case "update_error":
          return {
            title: intl.formatMessage(messages.updateErrorTitle),
            text: intl.formatMessage(messages.updateErrorDescription),
            confirmButtonColor: APP_COLORS.primary,
            icon: "error",
          };
        default:
          throw new Error(`Invalid action ${action}`);
      }
    },
    [intl]
  );

  const showModal = useCallback(
    async (action: InsuranceModalAction, onConfirm?: () => void) => {
      const result = await SweetAlert.fire(getModalPropsByAction(action));
      if (onConfirm && (result.isConfirmed || result.isDismissed || result.isDenied)) {
        onConfirm();
      }
    },
    [getModalPropsByAction]
  );

  return { showModal };
}
